import { Toast } from 'primereact/toast';
import { createContext, useContext, useMemo, useRef, type ReactNode } from 'react';

export interface ToastApi {
  success: (message: string, title?: string) => void;
  error: (message: string, title?: string) => void;
  info: (message: string, title?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const ref = useRef<Toast>(null);
  const api = useMemo<ToastApi>(() => {
    const show = (severity: 'success' | 'error' | 'info', detail: string, summary?: string) => {
      ref.current?.show({ severity, summary, detail, life: severity === 'error' ? 6000 : 3500 });
    };
    return {
      success: (m, t) => {
        show('success', m, t);
      },
      error: (m, t) => {
        show('error', m, t);
      },
      info: (m, t) => {
        show('info', m, t);
      },
    };
  }, []);
  return (
    <ToastContext.Provider value={api}>
      <Toast ref={ref} position="top-right" />
      {children}
    </ToastContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- hook belongs with its provider
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
